// libraries
import type { IDealerFee } from '@makemydeal/dr-platform-shared';
import { extractNumberFromString, formatDollarsAndCents, formatPercentage } from '@makemydeal/dr-common-utils';
import type { RegionBreakdown, LineItemCategory, AmountCategory } from '@makemydeal/dr-platform-types';
import { TaxLabelMappings } from '@makemydeal/dr-platform-types';
import {
    labelStyles as defaultLabelStyles,
    detailedViewLabelStyles,
    accordionLabelStyles,
    nestedLabelNodeStyles
} from '../utils/styles';

// components
import type { LineItem } from '../../types/LineItemTypes';

// interfaces/types
import type { DraftDealProduct } from '@makemydeal/dr-dash-types';
import type * as compositeSelectors from '@makemydeal/dr-dash-store/selectors/compositeSelectors';

import { ZERO_DOLLARS } from '../../constants';

type CappedDealerFeeBreakdown = {
    cappedFees: IDealerFee[];
    cappedFeeTotal: number;
};

export const convertToDollarCentsLineItem = (label: string, amount?: AmountCategory): LineItemCategory => ({
    label,
    value: formatDollarsAndCents(amount)
});

export const convertDealerFeeToLineItem = ({ dealerFeeName, dealerFeeAmount }: compositeSelectors.DealerFee): LineItemCategory =>
    convertToDollarCentsLineItem(dealerFeeName, dealerFeeAmount);

export const convertVppProductToLineItem = ({
    productName,
    productPrice,
    productCategoryCode
}: DraftDealProduct): LineItemCategory => {
    const label = productName || productCategoryCode || 'Product';
    const amount = productPrice;

    return convertToDollarCentsLineItem(label, amount);
};

export const convertAccessoryToLineItem = ({ name, amount }: any): LineItemCategory => convertToDollarCentsLineItem(name, amount);

export const convertGovernmentFeeToLineItem = ({ name, amount }: any): LineItemCategory =>
    convertToDollarCentsLineItem(name, amount);

export const convertLenderFeeToLineItem = ({ dealerFeeName, dealerFeeAmount }: compositeSelectors.DealerFee): LineItemCategory =>
    convertToDollarCentsLineItem(dealerFeeName, dealerFeeAmount);

/**
 * Converts objects with string-amount mappings to LineItem[] for use in the deal summary
 * 0 value mappings are not included in the resulting array
 */
export const taxElementsToLineItems = (taxElements?: RegionBreakdown): LineItem[] => {
    if (!taxElements) {
        return [];
    }
    const lineItems: LineItem[] = [];
    Object.keys(taxElements).forEach((key: string) => {
        if (taxElements[key]) {
            lineItems.push(convertToDollarCentsLineItem(TaxLabelMappings[key as keyof typeof TaxLabelMappings], taxElements[key]));
        }
    });
    return lineItems;
};

const initialBreakdown = () => ({ cappedFees: [], cappedFeeTotal: 0 });

export const getCappedFeeBreakdown = (dealerFees: IDealerFee[]): CappedDealerFeeBreakdown =>
    dealerFees.reduce((acc: CappedDealerFeeBreakdown, dealerFee: IDealerFee): CappedDealerFeeBreakdown => {
        if (dealerFee.dealerFeeCapped && dealerFee.dealerFeeAmount > 0) {
            acc.cappedFees.push(dealerFee);
            acc.cappedFeeTotal += dealerFee.dealerFeeAmount;
        }
        return acc;
    }, initialBreakdown());

export const lineItemWithChildrenAdaptor = (
    collection: Record<string, any>[],
    displayOptions: { key: string; label: string; value: string }
) => {
    return collection.map((item) => {
        return {
            key: item[displayOptions.key],
            label: item[displayOptions.label],
            value: item[displayOptions.value]
        };
    });
};

export const getStrAmountsWithoutSymbols = (items: LineItem[]) => {
    return items.map((item) => {
        const value = item.value != null ? String(item.value) : '';
        return {
            ...item,
            // remove symbols from string value and convert back to string
            value: extractNumberFromString(value).toString()
        };
    });
};

type ItemsWithTaxNames = { taxName: string }[];
type TaxNames = string[];

export const removeTaxesByName = (collection: ItemsWithTaxNames, taxNames: TaxNames) => {
    return collection.filter((tax) => !taxNames.includes(tax.taxName));
};

export const getLabelStyles = (showDetailedView: boolean, hasItemizedData: boolean) => {
    if (showDetailedView) return detailedViewLabelStyles;
    return hasItemizedData ? defaultLabelStyles : accordionLabelStyles;
};

export const getLabelNodeStyles = (showDetailedView: boolean, isExpandedNode: boolean) => ({
    ...accordionLabelStyles,
    ...(!showDetailedView && isExpandedNode ? defaultLabelStyles : isExpandedNode ? {} : nestedLabelNodeStyles)
});

export const getValueByUnit = (value: number | string, unit?: string) => {
    if (unit === '%') {
        return formatPercentage(value);
    }
    return formatDollarsAndCents(value, ZERO_DOLLARS);
};

export const assignFallbackLabels = <T>(
    items: T[],
    getLabel: (item: T) => string | undefined,
    setLabel: (item: T, label: string) => T,
    fallbackPrefix: string
): T[] => {
    return items.map((item, index) => {
        const label = getLabel(item);
        if (!label) {
            return setLabel(item, `${fallbackPrefix} ${index + 1}`);
        }
        return item;
    });
};

export const assignVppFallbackLabels = (products: DraftDealProduct[]): DraftDealProduct[] => {
    const categoryCounters: Record<string, number> = {};
    return products.map((product) => {
        if (!product.productName) {
            const code = product.productCategoryCode || 'Product';
            categoryCounters[code] = (categoryCounters[code] ?? 0) + 1;
            return { ...product, productName: `${code} ${categoryCounters[code]}` };
        }
        return product;
    });
};
